import type { Panel } from "@/app/config/form-config";
import { ENV, URLS } from "@/app/config/env";
import dynamic from "next/dynamic";
import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import { MultipleChoicePanel } from "./MultipleChoicePanel";
import { getSubjectLabel } from "@/app/config/slug-form-configs";
import type { SchoolDistrict } from "@/app/data/school-districts";
import { submitFormToCRM } from "@/app/actions/form-actions";
import { setScheduleHandoffCookie } from "@/app/actions/schedule-callback-actions";
import { getVisitorId } from "@/app/utils/visitor-tracking";
import { convertNumericDiscountToWords } from "@/app/utils/discount-converter";
import { useFormProgress } from '../FormProgressContext';
import { trackLeadFormShown, trackLeadFormStepCompleted, trackLeadFormStepViewed, trackLeadFormSubmitAttempted, type LeadFormStep } from "@/app/lib/lead-form-analytics";

const PanelLoadingShell = () => (
  <div className="form-panel animate-pulse" aria-live="polite" aria-label="Loading next question">
    <div className="h-32" />
  </div>
);

// Lazy-loaded panel components - these are not needed for initial render
// Reduces main thread work by deferring parsing/evaluation until needed
const loadTextPanel = () => import("./TextPanel").then(m => m.TextPanel);
const loadSchoolDistrictPanel = () => import("./SchoolDistrictPanel").then(m => ({ default: m.SchoolDistrictPanel }));
const loadPartnerSchoolPanel = () => import("./PartnerSchoolPanel").then(m => ({ default: m.PartnerSchoolPanel }));
const loadInterstitialPanel = () => import("./InterstitialPanel").then(m => ({ default: m.InterstitialPanel }));
const loadKidGateBlockPanel = () => import("./KidGateBlockPanel").then(m => ({ default: m.KidGateBlockPanel }));
const loadMultiSelectPanel = () => import("./MultiSelectPanel").then(m => ({ default: m.MultiSelectPanel }));

const TextPanel = dynamic(loadTextPanel, {
  loading: () => <PanelLoadingShell />,
});
const SchoolDistrictPanel = lazy(loadSchoolDistrictPanel);
const PartnerSchoolPanel = lazy(loadPartnerSchoolPanel);
const InterstitialPanel = lazy(loadInterstitialPanel);
const KidGateBlockPanel = lazy(loadKidGateBlockPanel);
const MultiSelectPanel = lazy(loadMultiSelectPanel);

const preloadDeferredPanels = () => {
  void loadTextPanel();
  void loadSchoolDistrictPanel();
  void loadPartnerSchoolPanel();
  void loadInterstitialPanel();
  void loadKidGateBlockPanel();
  void loadMultiSelectPanel();
};

let leadTrackingPromise: Promise<typeof import('@varsitytutors/lead-tracking')> | undefined;

const loadLeadTracking = () => {
  leadTrackingPromise ??= import('@varsitytutors/lead-tracking');
  return leadTrackingPromise;
};

const DEFAULT_REDIRECT_URL = '/thank-you';
const isInterstitialPanel = (panel: Panel): boolean => panel.type === "interstitial";
const createFallbackLeadEventId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `lead-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

// Interface for the component props
interface InteractiveFormProps {
  serverLocationHeadline?: string;
  schoolDistrictDiscounts?: { [districtId: string]: number };
  customFormConfig?: {
    panels?: string[];
    order?: string[];
  };
  resolvedPanels: Panel[];
  redirectUrl?: string;
  subject?: string | null;
  type?: string | null;
  location?: string | null;
  headlineOverride?: string;
  firstQuestionOverride?: string;
  assumedGradeLevel?: string;
  prefilledZipCode?: string;
  districtNameOverride?: string;
  discountOverride?: string;
  applicationId?: string;
}

export const InteractiveForm = ({ serverLocationHeadline, schoolDistrictDiscounts = {}, resolvedPanels, redirectUrl: defaultRedirectUrl = DEFAULT_REDIRECT_URL, subject, type, location, headlineOverride, firstQuestionOverride, assumedGradeLevel, prefilledZipCode, districtNameOverride, discountOverride, applicationId = 'vt-df-nov14' }: InteractiveFormProps) => {
  const { updateProgress, updateHeadline, setBackHandler, updateAnswers } = useFormProgress();
  // Bypass useLeadTracking to avoid its internal initializeAttribution() call,
  // which writes a duplicate touch into vt_touch_history alongside the
  // marketing-attribution script's autoboot. The script owns attribution
  // capture; we only need the event-id + Meta-Pixel + dataLayer primitives
  // here (mirrors the meta-leadform reference).
  const eventIdRef = useRef<string | null>(null);
  const pixelInitializedRef = useRef(false);
  const deferredPanelsPreloadedRef = useRef(false);

  const getLeadEventId = useCallback(async (): Promise<string> => {
    if (eventIdRef.current) {
      return eventIdRef.current;
    }

    try {
      const { initializeEventId } = await loadLeadTracking();
      eventIdRef.current = initializeEventId('Lead');
    } catch {
      // CRM submission must not depend on a deferred analytics chunk loading.
      eventIdRef.current = createFallbackLeadEventId();
    }

    return eventIdRef.current;
  }, []);

  const handlePreloadDeferredPanels = useCallback(() => {
    if (deferredPanelsPreloadedRef.current) return;
    deferredPanelsPreloadedRef.current = true;
    preloadDeferredPanels();
  }, []);

  useEffect(() => {
    if (pixelInitializedRef.current || !ENV.META_PIXEL_ID) return;
    pixelInitializedRef.current = true;

    let timer: number | undefined;
    let cancelled = false;

    void loadLeadTracking().then(({ initializeEventId, loadMetaPixelScript, setupPixelStub }) => {
      if (cancelled) return;

      eventIdRef.current ||= initializeEventId('Lead');
      setupPixelStub();
      window.fbq('init', ENV.META_PIXEL_ID);
      window.fbq('track', 'PageView');

      timer = window.setTimeout(() => {
        loadMetaPixelScript(ENV.META_PIXEL_ID);
      }, 2000);
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);
  
  // Helper to resolve the logical answer key for a panel and to read answers safely
  const getPanelAnswerKey = (panel: Panel): string => (panel as any).key || panel.id;
  const getAnswer = (key: string): string | undefined => {
    return answers[key] ?? (answers as any)[`${key}-reg`] ?? (answers as any)[`${key}-default`];
  };

  // Evaluate a panel's optional conditional visibility (renderWhen)
  // Supports a single condition or an array of conditions (AND logic).
  const evaluateRenderWhenWith = (
    panel: Panel,
    answersMap: Record<string, string>
  ): boolean => {
    const raw = (panel as any).renderWhen;
    if (!raw) return true;
    const conditions: Array<{ key: string; equals?: string; in?: string[]; notEquals?: string }> =
      Array.isArray(raw) ? raw : [raw];
    for (const condition of conditions) {
      const value = answersMap[condition.key];
      if (condition.equals !== undefined && value !== condition.equals) return false;
      if (condition.notEquals !== undefined && value === condition.notEquals) return false;
      if (condition.in && (!value || !condition.in.includes(value))) return false;
    }
    return true;
  };

  const isPanelVisible = (panel: Panel): boolean => evaluateRenderWhenWith(panel, answers);
  
   // Panels are resolved on the server so the large panel registry/config corpus
   // does not become part of the initial client form bundle.
   const customizedPanels = useMemo((): Panel[] => resolvedPanels, [resolvedPanels]);
  
  const [currentPanelIndex, setCurrentPanelIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // Sync answers into FormProgressContext so sibling components (e.g. variant B's
  // ProfileCard) can render based on what the user has typed.
  useEffect(() => {
    updateAnswers(answers);
  }, [answers, updateAnswers]);
  const [panelPositions, setPanelPositions] = useState<Record<number, 'center' | 'left' | 'right'>>({ 0: 'center' });
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'forward' | 'backward' | null>(null);
  const [availableDistricts, setAvailableDistricts] = useState<SchoolDistrict[]>([]);
  const [hasPublicSchools, setHasPublicSchools] = useState<boolean | null>(null);
  const [checkingPublicSchools, setCheckingPublicSchools] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  // Ref to track the last panel that was tracked to prevent duplicate tracking
  const lastTrackedPanelRef = useRef<string | null>(null);

  // If a slug provides an assumed grade level (e.g., SAT = High School), seed it into answers
  useEffect(() => {
    if (!assumedGradeLevel) return;
    setAnswers(prev => {
      if (prev["grade-level"] || prev["grade-level-hs-col"]) return prev;
      return { ...prev, ["grade-level"]: assumedGradeLevel };
    });
  }, [assumedGradeLevel]);

  // If a prefilled zip code is provided (e.g., for district pages), seed it into answers
  useEffect(() => {
    if (!prefilledZipCode) return;
    setAnswers(prev => {
      if (prev["zipcode"]) return prev;
      return { ...prev, ["zipcode"]: prefilledZipCode };
    });
  }, [prefilledZipCode]);
  
   // Use override, then server-provided headline, then default
   const [dynamicHeadline, setDynamicHeadline] = useState<string>(
    headlineOverride || serverLocationHeadline || ''
  );

  // Set initial headline in context on mount
  useEffect(() => {
    const initialHeadline = headlineOverride || serverLocationHeadline || '';
    updateHeadline(initialHeadline);
  }, [updateHeadline]); // Run on mount and when updateHeadline changes

  // Keep dynamic headline in sync if inputs change (override should take precedence)
  useEffect(() => {
    const next = headlineOverride || serverLocationHeadline || '';
    setDynamicHeadline(next);
    updateHeadline(next);
  }, [headlineOverride, serverLocationHeadline, updateHeadline]);

  const currentPanel = customizedPanels[currentPanelIndex];
  const isFirstPanel = currentPanelIndex === 0;

  // The PostHog lead form funnel for this page view. Declared before the panel view tracking below so it
  // runs first: the first panel's view is the funnel's lead_form_started.
  useEffect(() => {
    trackLeadFormShown({ variant: 'standard', subject }, { panelCount: customizedPanels.filter((panel) => !isInterstitialPanel(panel)).length });
    // Once per page view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track panel views with GTM only (remove web analytics tracking)
  useEffect(() => {
    if (currentPanel) {
      // Only track non-interstitial panels
      if (!isInterstitialPanel(currentPanel)) {
        // Skip tracking partner-school panel if it should be skipped
        if (currentPanel.type === "partner-school") {
          const selectedDistrictId = getAnswer("school-district");
          const isPrivateOrOther = selectedDistrictId === "private" || selectedDistrictId === "other";
          const gradeLevel = getAnswer("grade-level") || assumedGradeLevel;
          const gradeLevelHsCol = getAnswer("grade-level-hs-col");
          const isK12 = gradeLevel === "Elementary school" || gradeLevel === "Middle school" || gradeLevel === "High School" || gradeLevelHsCol === "High School";
          
          // Skip tracking if not K-12 OR if Private School/Other is selected
          if (!isK12 || isPrivateOrOther) {
            return; // Don't track this panel view
          }
        }
        
        // Only track if this panel hasn't been tracked yet (prevents duplicate tracking)
        if (lastTrackedPanelRef.current !== currentPanel.id) {
          void import("@/app/utils/analytics").then(({ trackPanelView }) => {
            trackPanelView(currentPanel.id);
          });
          // The PostHog lead form funnel (the same events as the guided chat).
          trackLeadFormStepViewed(leadFormStep(currentPanel, progressInfo.currentPanel));
          lastTrackedPanelRef.current = currentPanel.id;
        }
      }
    }
  }, [currentPanelIndex, currentPanel, answers, assumedGradeLevel]);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set the initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate the progress for the progress bar
  const progressInfo = useMemo(() => {
    // Count total non-interstitial panels that are currently visible
    const nonInterstitialPanels = customizedPanels.filter(panel => !isInterstitialPanel(panel) && isPanelVisible(panel));
    const totalPanels = nonInterstitialPanels.length;
    
    // Calculate the current panel number (excluding interstitials and hidden panels)
    let currentNonInterstitialIndex = 0;
    
    // Count panels we've passed through (excluding interstitials and hidden)
    for (let i = 0; i < currentPanelIndex; i++) {
      const p = customizedPanels[i];
      if (!isInterstitialPanel(p) && isPanelVisible(p)) {
        currentNonInterstitialIndex++;
      }
    }
    
    // Add the current panel if it's not an interstitial and visible
    if (!isInterstitialPanel(currentPanel) && isPanelVisible(currentPanel)) {
      currentNonInterstitialIndex++;
    }
    
    // Calculate how many panels we've actually completed
    let completedPanels = currentNonInterstitialIndex - 1; // -1 because we haven't completed the current panel yet
    
    // Handle the school district panel skipping logic
    // If we're past the zipcode panel and there are no public schools, 
    // we count as if we've passed the school district panel too
    const zipCodePanelIndex = nonInterstitialPanels.findIndex(panel => panel.type === "zipcode");
    const schoolDistrictPanelIndex = nonInterstitialPanels.findIndex(panel => panel.type === "school-district");
    
    if (currentNonInterstitialIndex > zipCodePanelIndex + 1 && 
        hasPublicSchools === false && 
        schoolDistrictPanelIndex > zipCodePanelIndex) {
      // We're past the zip code panel, school panel exists after zip, but no public schools
      // So we add 1 to the completed panels as if we went through the school panel
      completedPanels++;
    }
    
    // Calculate the percentage progress
    const percentage = totalPanels <= 1 
      ? 0 
      : Math.min(100, Math.round((completedPanels / totalPanels) * 100));
    
    return {
      currentPanel: currentNonInterstitialIndex,
      totalPanels,
      completedPanels,
      percentage
    };
  }, [currentPanelIndex, currentPanel, hasPublicSchools, answers]);

  // A panel as the PostHog lead form funnel names it (lead-form-analytics.ts).
  const leadFormStep = (panel: Panel, number: number): LeadFormStep => ({
    panelId: panel.id,
    key: getPanelAnswerKey(panel),
    number,
    type: panel.type,
    question: (panel as { question?: string }).question,
  });

  // Update the global progress context whenever our local progress changes
  useEffect(() => {
    updateProgress(progressInfo.currentPanel, progressInfo.totalPanels, progressInfo.percentage);
  }, [progressInfo, updateProgress]);

  // Check if zip code has public schools
  useEffect(() => {
    const zipCode = getAnswer("zipcode");
    const gradeLevel = getAnswer("grade-level") || assumedGradeLevel;
    const gradeLevelHsCol = getAnswer("grade-level-hs-col");
    const isK12 = gradeLevel === "Elementary school" || gradeLevel === "Middle school" || gradeLevel === "High School" || gradeLevelHsCol === "High School";
    
    if (zipCode && zipCode.length === 5) {
      setCheckingPublicSchools(true);
      
      // Only check for public schools if the grade level is K-12
      if (isK12) {
        import("@/app/services/school-district.service")
          .then(({ schoolDistrictService }) => schoolDistrictService.hasPublicSchoolsForZip(zipCode))
          .then(hasPublic => {
            setHasPublicSchools(hasPublic);

            // If we're already at the school district panel but there are no public schools,
            // automatically move to the next panel that isn't a school district or partner school panel
            if (currentPanel.type === "school-district" && !hasPublic) {
              const nextNonSchoolPanelIndex = findNextNonSchoolDistrictPanel(currentPanelIndex);
              setCurrentPanelIndex(nextNonSchoolPanelIndex);
            }
          })
          .catch(() => {
            setHasPublicSchools(false);
          })
          .finally(() => {
            setCheckingPublicSchools(false);
          });
      } else {
        // For non-K12 students, skip the school district check and set hasPublicSchools to false
        setHasPublicSchools(false);
        setCheckingPublicSchools(false);
      }
    }
  }, [answers, currentPanel.type, currentPanelIndex, assumedGradeLevel]);

  // Load school districts when zip code is entered
  useEffect(() => {
    const zipCode = getAnswer("zipcode");
    if (zipCode && currentPanel.type === "school-district") {
      import("@/app/services/school-district.service")
        .then(({ schoolDistrictService }) => schoolDistrictService.getDistrictsForZip(zipCode))
        .then(districts => {
          setAvailableDistricts(districts);
        })
        .catch(() => {
          // If there's an error, only show private and other options
          setAvailableDistricts([
            { id: "private", name: "Private School", isPublic: false },
            { id: "other", name: "Other", isPublic: false }
          ]);
        });
    }
  }, [currentPanel.type, answers]);

  // Find the next panel index that isn't a school district or partner school
  const findNextNonSchoolDistrictPanel = (currentIndex: number): number => {
    for (let i = currentIndex + 1; i < customizedPanels.length; i++) {
      if (customizedPanels[i].type !== "school-district" && 
          customizedPanels[i].type !== "partner-school") {
        return i;
      }
    }
    // If no other panels found, return the next panel
    return currentIndex + 1;
  };

  // Listen to browser back/forward navigation and update current panel accordingly
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#get-started/')) {
        const parts = hash.split('/');
        const panelNumber = parseInt(parts[1], 10);
        if (!isNaN(panelNumber)) {
          const newPanelIndex = panelNumber - 1;
          setCurrentPanelIndex(newPanelIndex);
          // Also update panelPositions to ensure correct rendering
          setPanelPositions({ [newPanelIndex]: 'center' });
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Always normalize the URL hash to '#get-started/1' on mount.
  // We don't persist `answers` across reloads, so `currentPanelIndex` is always
  // 0 here — any other hash (e.g. left over from a previous session, browser
  // back from thank-you, tab restore) is stale. Leaving it would let the
  // popstate handler jump the user to a high panel index with empty answers
  // and ship a name-less submission (MEX-15440).
  useEffect(() => {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}#get-started/1`);
  }, []);

  // Landing page + click ID + UTM capture is handled by the marketing-attribution
  // script (autoboot in app/layout.tsx). No first-render hook needed.

  const handleAnswer = useCallback(async (value: string) => {
    if (isAnimating) return;
    handlePreloadDeferredPanels();
    
    // Check if value is a JSON string (from phone number panel with consent)
    let parsedValue: string | Record<string, string> = value;
    let newAnswers = { ...answers };
    
    try {
      // Try to parse as JSON
      if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
        parsedValue = JSON.parse(value);
        
        // If it's an object, add each property to answers
        if (typeof parsedValue === 'object' && parsedValue !== null) {
          // Remap current panel id to canonical key if present
          const answerKey = getPanelAnswerKey(currentPanel);
          const currentId = currentPanel.id;
          const remapped: Record<string, string> = { ...parsedValue } as any;
          if ((remapped as any)[currentId] !== undefined && answerKey !== currentId) {
            remapped[answerKey] = (remapped as any)[currentId];
            delete (remapped as any)[currentId];
          }
          newAnswers = { ...answers, ...remapped };
        }
      } else {
        // If it's not JSON, just add the value normally
        const answerKey = getPanelAnswerKey(currentPanel);
        newAnswers = { ...answers, [answerKey]: value };
      }
    } catch (e) {
      // If parsing fails, just use the original value
      const answerKey = getPanelAnswerKey(currentPanel);
      newAnswers = { ...answers, [answerKey]: value };
    }

    // CLIENT-SIDE K-12 VALIDATION (MEX-XXXXX)
    // Block adult self-learners on K-12 subject pages to train Meta algorithm
    const currentAnswerKey = getPanelAnswerKey(currentPanel);
    if (currentAnswerKey === 'who-needs-help') {
      // Get the audience for the current page
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const { getSubjectAndLTV } = await import("@/app/lib/ltv");
      const ltvData = getSubjectAndLTV({ pathname });

      if (ltvData.audience === 'k12') {
        // Check if this is an adult self-learner response
        const whoNeedsHelp = typeof parsedValue === 'string' ? parsedValue.toLowerCase() : value.toLowerCase();
        const isAdultSelfLearner =
          whoNeedsHelp === 'i do' ||
          whoNeedsHelp === 'myself' ||
          whoNeedsHelp === 'me';

        if (isAdultSelfLearner) {
          // Track rejection event for Meta to train algorithm
          void loadLeadTracking()
            .then(({ trackMetaEvent }) => {
              trackMetaEvent('LeadRejected', {
                rejection_reason: 'adult_k12_subject',
                subject: ltvData.subject || 'unknown',
                audience: ltvData.audience,
                who_needs_help: whoNeedsHelp,
              });
            })
            .catch(() => {});

          // Show error message and block form progression
          setSubmissionError(
            'This tutoring program is designed for K-12 students. If you\'re looking for adult tutoring, please visit our LSAT, MCAT, GRE, or GMAT pages.'
          );

          // Don't proceed with answer processing
          return;
        }
      }
    }

    // Clear any previous submission errors
    if (submissionError) {
      setSubmissionError(null);
    }

    // For the school-district panel, also persist the human-readable name so
    // downstream consumers (e.g. the variant B ProfileCard) can display it.
    if (currentPanel.type === 'school-district' && typeof value === 'string') {
      const matched = availableDistricts.find(d => d.id === value);
      if (matched) {
        newAnswers = { ...newAnswers, 'school-district-name': matched.name };
      }
    }

    setAnswers(newAnswers);

    // Track form step completion
    // Note: leadformVersion is intentionally empty per requirements
    void import("@/app/utils/web-analytics").then(({ trackFormStepComplete }) => {
      trackFormStepComplete(currentPanel.id);
    });
    trackLeadFormStepCompleted(leadFormStep(currentPanel, progressInfo.currentPanel), {
      // Only a plain answer (a choice); the phone panel sends its number and consent as JSON.
      answer: typeof parsedValue === 'string' ? parsedValue : undefined,
      inputMethod: 'easy_choice',
    });

    // Track Meta Lead Form Started event when first panel is answered
    if (currentPanelIndex === 0) {
      void loadLeadTracking()
        .then(({ trackMetaEvent }) => {
          trackMetaEvent('LeadFormStarted', { content_category: subject || 'general' }, { sessionDedupKey: 'meta_lead_form_started' });
        })
        .catch(() => {});
    }

    if (currentPanelIndex < customizedPanels.length - 1) {
      let nextIndex = currentPanelIndex + 1;

      // Get grade level and check if K-12
      const gradeLevel = newAnswers["grade-level"] || assumedGradeLevel;
      const gradeLevelHsCol = newAnswers["grade-level-hs-col"];
      const isK12 = gradeLevel === "Elementary school" || gradeLevel === "Middle school" || gradeLevel === "High School" || gradeLevelHsCol === "High School";

      // Gate K-12 flow strictly on ZIP presence
      if (customizedPanels[nextIndex].type === "school-district" && !newAnswers["zipcode"]) {
        nextIndex++;
      }
      if (customizedPanels[nextIndex].type === "partner-school" && !newAnswers["zipcode"]) {
        nextIndex++;
      }
      if (customizedPanels[nextIndex].type === "interstitial" && !newAnswers["zipcode"]) {
        nextIndex++;
      }
      
      // Skip school district panel if grade level is not K-12
      if (customizedPanels[nextIndex].type === "school-district" && !isK12) {
        nextIndex = findNextNonSchoolDistrictPanel(currentPanelIndex);
      }
      
      // Do not skip for public school availability; K-12 should see these panels when present
      
      // Skip partner school panel if grade level is not K-12
      if (customizedPanels[nextIndex].type === "partner-school" && !isK12) {
        nextIndex++;
      }

      // Skip any panels hidden by conditional renderWhen
      while (nextIndex < customizedPanels.length && !evaluateRenderWhenWith(customizedPanels[nextIndex], newAnswers)) {
        nextIndex++;
      }

      setIsAnimating(true);
      setAnimationDirection('forward');

      // Set up initial positions
      setPanelPositions(prev => ({
        ...prev,
        [nextIndex]: 'right'
      }));


      // Start the animation sequence
      requestAnimationFrame(() => {
        setPanelPositions(prev => ({
          ...prev,
          [currentPanelIndex]: 'left'
        }));

          // After current panel is out, bring in next panel
          setTimeout(() => {
          setPanelPositions(prev => ({
            ...prev,
            [nextIndex]: 'center'
          }));

          // After both animations complete, clean up
            setTimeout(() => {
            setCurrentPanelIndex(nextIndex);
            setIsAnimating(false);
            setAnimationDirection(null);
            setPanelPositions({ [nextIndex]: 'center' });
            
            // Scroll to top after slide transition
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Update the URL hash with new panel, preserving pathname + search params (gclid, UTMs, etc.)
            history.pushState(null, '', `${window.location.pathname}${window.location.search}#get-started/${nextIndex + 1}`);
            }, customizedPanels[currentPanelIndex].type === 'interstitial' ? 50 : 250);
          }, customizedPanels[currentPanelIndex].type === 'interstitial' ? 50 : 100);
      });
    } else {
      // This is the final panel, submit the form
      handleFormSubmission(newAnswers);
    }
  }, [isAnimating, handlePreloadDeferredPanels, answers, currentPanel, currentPanelIndex, customizedPanels, assumedGradeLevel, availableDistricts, progressInfo]);

  const handleBack = useCallback(() => {
    if (isAnimating || currentPanelIndex <= 0) {
      return;
    }
    
    let prevIndex = currentPanelIndex - 1;

    // Always skip interstitial panels when going back
    if (customizedPanels[prevIndex]?.type === "interstitial") {
      prevIndex--;
    }

    // Skip partner school panel when going back if it wasn't shown
    // (only shown if a district was selected)
    if (customizedPanels[prevIndex]?.type === "partner-school") {
      prevIndex--;
    }

    // Skip school district panel when going back if no zip code or no public schools
    // This mirrors the forward logic where we skip if hasPublicSchools is false
    const hasZipCode = !!getAnswer("zipcode");
    if (customizedPanels[prevIndex]?.type === "school-district" && 
        (!hasZipCode || hasPublicSchools === false)) {
      prevIndex--;
    }

    // Skip panels hidden by conditional renderWhen when going back
    while (prevIndex >= 0 && !evaluateRenderWhenWith(customizedPanels[prevIndex], answers)) {
      prevIndex--;
    }

    // Safety check to prevent going back past the first panel
    if (prevIndex < 0) {
      prevIndex = 0;
    }
    
    setIsAnimating(true);
    setAnimationDirection('backward');

    // Set up initial positions
    setPanelPositions(prev => ({
      ...prev,
      [prevIndex]: 'left'
    }));

    // Start the animation sequence
    requestAnimationFrame(() => {
      
      setPanelPositions(prev => ({
        ...prev,
        [currentPanelIndex]: 'right'
      }));

      // After current panel is out, bring in previous panel
      setTimeout(() => {
        setPanelPositions(prev => ({
          ...prev,
          [prevIndex]: 'center'
        }));

        // After both animations complete, clean up
        setTimeout(() => {
          setCurrentPanelIndex(prevIndex);
          setIsAnimating(false);
          setAnimationDirection(null);
          setPanelPositions({ [prevIndex]: 'center' });
          
          // Scroll to top after slide transition
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 250);
      }, 100);
    });
  }, [isAnimating, currentPanelIndex, customizedPanels, answers, hasPublicSchools]);

  // Update the back handler in the progress context
  useEffect(() => {
    const showBack = currentPanelIndex > 0;
    setBackHandler(showBack ? handleBack : undefined, showBack);
  }, [currentPanelIndex, handleBack, setBackHandler]);

  const getTransformClass = (position: 'center' | 'left' | 'right') => {
    switch (position) {
      case 'left': return '-translate-x-[150%]';
      case 'right': return 'translate-x-full';
      default: return 'translate-x-0';
    }
  };


  const renderPanel = (panelIndex: number) => {
    const panel = customizedPanels[panelIndex];
    const isPanelAfterFirst = panelIndex > 0;
    const position = panelPositions[panelIndex] || 'right';
    
    // Generic conditional rendering gate
    if (!isPanelVisible(panel)) {
      setTimeout(() => {
        if (!isAnimating && currentPanelIndex === panelIndex) {
          let nextIndex = panelIndex + 1;
          // Advance to the next visible panel
          while (nextIndex < customizedPanels.length && !evaluateRenderWhenWith(customizedPanels[nextIndex], answers)) {
            nextIndex++;
          }
          setCurrentPanelIndex(nextIndex);
        }
      }, 0);
      return (
        <div className="flex flex-col items-center justify-center p-8">
          <p className="text-sm text-muted-foreground mt-2">Proceeding to the next step...</p>
        </div>
      );
    }
    
    // Check grade level for K-12
    const gradeLevel = getAnswer("grade-level") || assumedGradeLevel;
    const gradeLevelHsCol = getAnswer("grade-level-hs-col");
    const isK12 = gradeLevel === "Elementary school" || gradeLevel === "Middle school" || gradeLevel === "High School" || gradeLevelHsCol === "High School";
    
    // Skip rendering school district panel if not K-12 or if checking or no public schools
    if (panel.type === "school-district") {
      // If not K-12, skip this panel
      if (!isK12) {
        setTimeout(() => {
          if (!isAnimating && currentPanelIndex === panelIndex) {
            const nextIndex = findNextNonSchoolDistrictPanel(panelIndex);
            setCurrentPanelIndex(nextIndex);
          }
        }, 0);
        
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <p className="text-lg">School district selection not needed.</p>
            <p className="text-sm text-muted-foreground mt-2">Proceeding to the next step...</p>
          </div>
        );
      }
      
      if (checkingPublicSchools) {
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        );
      }
      
    }
    
    // Skip partner school panel if not K-12 or if Private School/Other is selected
    if (panel.type === "partner-school") {
      const selectedDistrictId = getAnswer("school-district");
      const isPrivateOrOther = selectedDistrictId === "private" || selectedDistrictId === "other";
      
      // Skip if not K-12 OR if Private School/Other is selected (these are already partnered)
      if (!isK12 || isPrivateOrOther) {
        setTimeout(() => {
          if (!isAnimating && currentPanelIndex === panelIndex) {
            setCurrentPanelIndex(panelIndex + 1);
          }
        }, 0);
        
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <p className="text-sm text-muted-foreground mt-2">Proceeding to the next step...</p>
          </div>
        );
      }
      
    }
    
    switch (panel.type) {
      case "multiple-choice":
        // If this is the intro "who-needs-help" logical key and we have an override, apply it
        const introKey = (panel as any).key || panel.id;
        let mcPanel = introKey === 'who-needs-help' && firstQuestionOverride
          ? ({ ...panel, question: firstQuestionOverride } as typeof panel)
          : panel;
        
        // Automatically replace dynamic placeholders in panel question
        let dynamicQuestion = mcPanel.question;
        if (subject && dynamicQuestion.includes('{Subject}')) {
          const subjectLabel = getSubjectLabel(subject);
          dynamicQuestion = dynamicQuestion.replace(/\{Subject\}/g, subjectLabel);
        }
        if (type && dynamicQuestion.includes('{Type}')) {
          dynamicQuestion = dynamicQuestion.replace(/\{Type\}/g, type);
        }
        if (location && dynamicQuestion.includes('{Location}')) {
          dynamicQuestion = dynamicQuestion.replace(/\{Location\}/g, location);
        }
        if (dynamicQuestion !== mcPanel.question) {
          mcPanel = { ...mcPanel, question: dynamicQuestion } as typeof mcPanel;
        }
        return (
          <MultipleChoicePanel
            panel={mcPanel}
            onAnswer={handleAnswer}
            onBack={handleBack}
            showBack={panelIndex > 0}
            isSliding={panelPositions[panelIndex] !== 'center'}
            isAfterFirstPanel={isPanelAfterFirst}
            initialValue={answers[getPanelAnswerKey(panel)] || null}
            locationHeadline={serverLocationHeadline}
          />
        );
      case "multi-select":
        let multiSelectPanel = panel;
        let multiSelectDynamicQuestion = multiSelectPanel.question;
        if (subject && multiSelectDynamicQuestion.includes('{Subject}')) {
          const subjectLabel = getSubjectLabel(subject);
          multiSelectDynamicQuestion = multiSelectDynamicQuestion.replace(/\{Subject\}/g, subjectLabel);
        }
        if (type && multiSelectDynamicQuestion.includes('{Type}')) {
          multiSelectDynamicQuestion = multiSelectDynamicQuestion.replace(/\{Type\}/g, type);
        }
        if (location && multiSelectDynamicQuestion.includes('{Location}')) {
          multiSelectDynamicQuestion = multiSelectDynamicQuestion.replace(/\{Location\}/g, location);
        }
        if (multiSelectDynamicQuestion !== multiSelectPanel.question) {
          multiSelectPanel = { ...multiSelectPanel, question: multiSelectDynamicQuestion } as typeof multiSelectPanel;
        }
        return (
          <MultiSelectPanel
            panel={multiSelectPanel}
            onAnswer={handleAnswer}
            onBack={handleBack}
            showBack={panelIndex > 0}
            isSliding={panelPositions[panelIndex] !== 'center'}
            isAfterFirstPanel={isPanelAfterFirst}
            initialValue={answers[getPanelAnswerKey(panel)] || ""}
          />
        );
      case "text":
      case "email":
      case "zipcode":
      case "phone_number":
        let textPanel = panel;
        let textDynamicQuestion = textPanel.question;
        if (subject && textDynamicQuestion.includes('{Subject}')) {
          const subjectLabel = getSubjectLabel(subject);
          textDynamicQuestion = textDynamicQuestion.replace(/\{Subject\}/g, subjectLabel);
        }
        if (type && textDynamicQuestion.includes('{Type}')) {
          textDynamicQuestion = textDynamicQuestion.replace(/\{Type\}/g, type);
        }
        if (location && textDynamicQuestion.includes('{Location}')) {
          textDynamicQuestion = textDynamicQuestion.replace(/\{Location\}/g, location);
        }
        if (textDynamicQuestion !== textPanel.question) {
          textPanel = { ...textPanel, question: textDynamicQuestion } as typeof textPanel;
        }
        return (
          <TextPanel
            panel={textPanel}
            onAnswer={handleAnswer}
            onBack={handleBack}
            showBack={panelIndex > 0}
            isAfterFirstPanel={isPanelAfterFirst}
            isSubmitting={panelIndex === customizedPanels.length - 1 ? isSubmitting : undefined}
            submissionError={panelIndex === customizedPanels.length - 1 ? submissionError : undefined}
            position={position}
            initialValue={answers[getPanelAnswerKey(panel)] || ""}
          />
        );
      case "school-district":
        return (
          <SchoolDistrictPanel
            panel={panel}
            onAnswer={handleAnswer}
            onBack={handleBack}
            showBack={panelIndex > 0}
            districts={availableDistricts}
            isSliding={panelPositions[panelIndex] !== 'center'}
            isAfterFirstPanel={isPanelAfterFirst}
            initialValue={answers[getPanelAnswerKey(panel)] || ""}
          />
        );
      case "partner-school":
        // Get the selected school district name
        const selectedDistrictId = getAnswer("school-district");
        const selectedDistrict = availableDistricts.find(d => d.id === selectedDistrictId);
        const selectedDistrictName = selectedDistrict?.name || "Your School District";
        
        // Retrieve the discount for the current school district
        // Default to undefined if not found, PartnerSchoolPanel will handle the 10% default
        const currentSchoolDiscount = selectedDistrictId ? schoolDistrictDiscounts[selectedDistrictId] : undefined;

        // Create a modified panel with the selected school name, helper content, and discount
        const modifiedPanel = {
          ...panel,
          selectedSchool: selectedDistrictName,
          discountPercentage: currentSchoolDiscount,
          helperContent: {
            paragraph: "You have access to a host of benefits including:",
            bullets: [
              "XX% off all tutoring programs",
              "Accelerated tutor placement times",
              "Dedicated customer support",
              "And more!"
            ],
            highlightedBulletIndex: 0
          }
        };
        
        return (
          <PartnerSchoolPanel
            panel={modifiedPanel}
            onAnswer={handleAnswer}
            onBack={handleBack}
            showBack={panelIndex > 0}
            isAfterFirstPanel={isPanelAfterFirst}
            initialValue={answers[getPanelAnswerKey(panel)] || ""}
          />
        );
      case "interstitial":
        // Check if this is a blocking kid-gate interstitial (MEX-14869)
        const panelContent = (panel as any).content;
        if (panel.id === 'kid-gate-block' || panelContent?.blockProgress) {
          return (
            <KidGateBlockPanel
              headline={panelContent?.headline}
              body={panelContent?.body}
            />
          );
        }
        return (
          <InterstitialPanel
            panel={panel}
            onAnswer={handleAnswer}
            isAfterFirstPanel={isPanelAfterFirst}
          />
        );
      default:
        return null;
    }
  };

  // Determine which panels to render based on animation direction
  const panelsToRender = isAnimating
    ? animationDirection === 'forward'
      ? [currentPanelIndex, currentPanelIndex + 1]
      : [currentPanelIndex - 1, currentPanelIndex]
    : [currentPanelIndex];

  // Filter out interstitial panels when going backward to prevent flashing
  const filteredPanelsToRender = panelsToRender.filter(index => {
    if (animationDirection === 'backward' && customizedPanels[index]?.type === 'interstitial') {
      return false;
    }
    return true;
  });

  // Check if current panel is an interstitial
  const isInterstitial = currentPanel.type === 'interstitial';


  // Submit the form data to the CRM API
  const handleFormSubmission = async (finalAnswers: Record<string, string>) => {
    // CAC-2508: never submit a contact request without a name. An in-session browser
    // back/forward popstate can leave the form on the final panel with the name unset
    // (the MEX-15440 mount-time hash normalization only covers fresh loads, not in-session
    // navigation), which creates a name-less contact request the backend rejects — a lost
    // lead that lands in the contact-request DLQ. Route the user back to the name step
    // instead of submitting.
    if (!finalAnswers["full-name"]?.trim()) {
      const nameIndex = customizedPanels.findIndex((p) => getPanelAnswerKey(p) === "full-name");
      if (nameIndex >= 0) {
        setCurrentPanelIndex(nameIndex);
        setPanelPositions({ [nameIndex]: "center" });
        history.replaceState(null, "", `${window.location.pathname}${window.location.search}#get-started/${nameIndex + 1}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // No name panel exists (shouldn't happen in the wizard) — surface a visible
        // error instead of leaving the submit button an inert no-op.
        setSubmissionError("Please enter your name before submitting.");
      }
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmissionError(null);

      // Inject subject from query params/URL if not already in answers (MEX-15352)
      // This ensures the subject extracted from ?vtsubject= or ?subject= flows to CRM
      if (subject && !finalAnswers["subject"]) {
        finalAnswers["subject"] = subject;
      }

      if (!window.MarketingVT) {
        const { captureMissingMarketingAttribution } = await import("@/app/utils/marketing-attribution-observability");
        captureMissingMarketingAttribution({
          origin: 'paid-leadform',
          page_url: window.location.href,
          user_agent: navigator.userAgent,
        });
      }

      const visitorId = getVisitorId();
      const persistentVisitorId = window.MarketingVT?.getPersistentVisitorId?.();
      const marketingAttribution = window.MarketingVT?.getAttribution?.();
      // The schoolDistrict value is taken directly from finalAnswers, 
      // matching the panel ID (e.g., "school-district")
      const schoolDistrict = finalAnswers["school-district"] || ""; 
      
      // Determine the referer
      const referer = document.referrer && document.referrer.trim() !== "" ? document.referrer : URLS.DEFAULT_REFERER;
      const currentPageUrl = window.location.href;

      // Convert numeric discount to words for CRM
      const districtId = finalAnswers["school-district"];
      const numericDiscount = 
        districtId && 
        schoolDistrictDiscounts && 
        schoolDistrictDiscounts[districtId] !== undefined
          ? schoolDistrictDiscounts[districtId]
          : undefined;
      
      const discountInWords = convertNumericDiscountToWords(numericDiscount);

      let finalAnswersDataString = "{}"; // Default to empty JSON string
      try {
        const tempFinalAnswers = JSON.parse(JSON.stringify(finalAnswers)); // Deep clone
        delete tempFinalAnswers["full-name"];
        delete tempFinalAnswers["email"];
        delete tempFinalAnswers["phone"];
        finalAnswersDataString = JSON.stringify(tempFinalAnswers);
      } catch (e) {
        finalAnswersDataString = JSON.stringify({ errorParsingForLog: "Could not parse or modify finalAnswers for logging" });
      }


      // Call the server action
      const eventId = await getLeadEventId();
      trackLeadFormSubmitAttempted({ stepCount: progressInfo.currentPanel });
      const result = await submitFormToCRM({
        form_variant: 'standard',
        answers: finalAnswers,
        visitorId,
        schoolDistrict, // Pass the school district ID as collected
        referer, // Add the referer to the payload
        discount_in_words: discountOverride || discountInWords, // Use override if provided (for district pages)
        application_id: applicationId, // Add application_id to the payload for CRM attribution
        page_url: currentPageUrl,
        district_name_override: districtNameOverride, // Override district name for district pages
        attribution: marketingAttribution, // From window.MarketingVT (canonical script-owned attribution)
        event_id: eventId, // From initializeEventId('Lead') (CAPI dedup + DB submission ID)
        persistentVisitorId, // From window.MarketingVT — durable identity for cross-session stitching
      });
      
      if (result.success) {
        let responseDataStringForLog = "{}"; // Default to empty JSON string if result.data is not present

        if (result.data) {
          try {
            // Create a deep copy to modify for logging
            const tempResponseData = JSON.parse(JSON.stringify(result.data));

            // Remove PII fields from the root of the copied data
            delete tempResponseData.phoneNumber;
            delete tempResponseData.emailAddress;

            // Remove PII fields from the nested 'data' object of the copied data
            if (tempResponseData.data) {
              delete tempResponseData.data.contact_details;
              delete tempResponseData.data.name;
            }
            responseDataStringForLog = JSON.stringify(tempResponseData);
          } catch (e) {
            // Log a specific error structure if parsing/modification fails
            responseDataStringForLog = JSON.stringify({ errorParsingForLog: "Could not parse or modify CRM response for logging" });
          }
        } else {
            // Handle case where result.data is null/undefined but success is true
            responseDataStringForLog = JSON.stringify({ crmSuccessNoData: "CRM call successful, but no data object was present in the response." });
        }
        
        try {
          // Track lead creation success with internal analytics
          const { trackLeadCreationSuccess } = await import("@/app/utils/web-analytics");
          await trackLeadCreationSuccess("no_subject_labor_ill_va_districts");
        } catch {
          // Do not block thank-you routing after CRM has accepted the lead.
        }

        try {
          // Push lead_created event to dataLayer for GTM
          const { pushToDataLayer, trackMetaEvent } = await loadLeadTracking();
          pushToDataLayer('lead_created', {
            leadData: {
              leadId: 'unknown',
              timestamp: new Date().toISOString(),
              source: window.location.href,
              visitorId,
            }
          });

          // Track Meta Lead event with event_id for Pixel deduplication
          await trackMetaEvent('Lead', {
            content_category: subject || 'general',
          }, {
            userData: {
              email: finalAnswers['email'],
              phone: finalAnswers['phone'],
              fullName: finalAnswers['full-name'],
              zipCode: finalAnswers['zipcode'],
            },
            eventId,
          });
        } catch {
          // Do not block thank-you routing after CRM has accepted the lead.
        }
        
        // After-hours: redirect to in-app scheduling page
        // During hours (or missing phone/zip): redirect to thank-you page
        let redirectUrl: string;
        const phone = finalAnswers['phone']?.replace(/\D/g, '') || '';
        const zip = finalAnswers['zipcode']?.trim() || '';
        let shouldScheduleAfterHours = false;

        if (phone && zip) {
          try {
            const [{ isForceAfterHoursEnabled }, { isAfterHoursForZip }] = await Promise.all([
              import("@/app/utils/after-hours-dev"),
              import("@/app/utils/business-hours"),
            ]);
            const forceAfterHours = isForceAfterHoursEnabled(new URLSearchParams(window.location.search));
            shouldScheduleAfterHours = forceAfterHours || isAfterHoursForZip(zip);
          } catch {
            shouldScheduleAfterHours = false;
          }
        }

        if (shouldScheduleAfterHours) {
          await setScheduleHandoffCookie(phone, zip);
          const currentParams = new URLSearchParams(window.location.search);
          redirectUrl = currentParams.toString()
            ? `/df/thank-you-after-hours?${currentParams.toString()}`
            : '/df/thank-you-after-hours';
        } else {
          const currentParams = new URLSearchParams(window.location.search);
          redirectUrl = currentParams.toString()
            ? `${defaultRedirectUrl}?${currentParams.toString()}`
            : defaultRedirectUrl;
        }
        window.location.href = redirectUrl;
        setSubmissionSuccess(true);
      } else {
        setSubmissionError(result.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setSubmissionError(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="leadform-wrapper">
      <div id="leadform-container" className={isFirstPanel ? 'first-panel' : 'other-panel'}>
        
        
        {/* Main content area - simplified and centered */}
        <div className="w-full relative">
          {filteredPanelsToRender.map(index => (
            <div
              key={index}
              id={`leadform-panel-${index}`}
              className={`${
                index === currentPanelIndex ? 'relative' : 'absolute top-0 left-0 w-full'
              } w-full transition-transform duration-[250ms] ease-in-out ${getTransformClass(panelPositions[index] || 'center')}`}
            >
              <Suspense fallback={<PanelLoadingShell />}>
                {renderPanel(index)}
              </Suspense>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}; 
